import {Collapse} from "@mui/material";
import {PracticeApptReason} from "@services/monolith/appointmentReasons";
import {ApptSlot} from "@services/monolith/availability";
import {compact} from "lodash";
import {useRouter} from "next/router";
import React, {RefObject} from "react";

import {QueryState} from "../../../hooks/useQueryController";
import {useTranslation} from "../../../i18n";
import {getSchedulingLink} from "../../../page-containers/booking/utils/bookingQuery";
import ScrollContainer from "../../../page-containers/get-care/components/ScrollContainer";
import {RootStateLocation} from "../../../store/types";
import {s3ImageSource} from "../../../useS3ImgSrc";
import ClinicListRow from "./ClinicListRow";
import {sendClinicListRowClickEvent, sendInitiateWebSchedulingEvent} from "./events";
import {LocSlotPair} from "./usePairLocsWithSlots";
import VirtualRow from "./VirtualRow";

type ListProps = {
  isSm: boolean;
  loading?: boolean;
  apptReason: PracticeApptReason;
  locations: LocSlotPair<RootStateLocation>[];
  firstLocRef: RefObject<HTMLLIElement>;
  soonestVirtualSlotQuery: QueryState<ApptSlot>;
  viewId?: string;
};

const getLocationImages = (location: RootStateLocation) =>
  // @ts-expect-error TS2532: Object is possibly 'undefined'.
  location?.images.slice(0, 2).map(img => ({src: s3ImageSource(img.imageId), alt: null})) || [];

const LocationList = ({
  isSm,
  loading,
  apptReason,
  locations,
  firstLocRef,
  soonestVirtualSlotQuery,
  viewId,
}: ListProps): React.ReactElement => {
  const {t} = useTranslation();
  const router = useRouter();
  const isVirtualOnly = locations.length === 0 && !loading;

  return (
    <ScrollContainer maxHeight="calc(82vh - 5vw)" disabled={isSm} containerTag="ul">
      {locations.map(({location, soonestSlot, specialtyId}, i) => {
        const isFirstLoc = i === 0;
        const images = isFirstLoc ? getLocationImages(location) : [];

        const value = t("Book");

        const onClick = async () => {
          if (soonestSlot) {
            const {bookingLink, flowId, WEB_SCHEDULING_ENABLED} = await getSchedulingLink({
              pathname: router.pathname,
              specialtyIds: compact([specialtyId]),
              webSchedulingQuery: {
                locationSlug: location.slug,
                appointmentReasonSlug: apptReason.slug,
                initialCalendarTime: soonestSlot?.time,
              },
              patientAppSchedulingQuery: {
                practiceId: location.practiceId,
                locationId: location.id,
                apptReasonId: apptReason.id,
                specialtyId,
              },
            });

            sendClinicListRowClickEvent(
              value,
              viewId,
              location.id,
              i + 1,
              soonestSlot?.time,
              location.dist,
              flowId || undefined,
            );

            if (WEB_SCHEDULING_ENABLED) {
              sendInitiateWebSchedulingEvent(flowId);
            }

            router.push(bookingLink);
          }
        };

        return (
          <Collapse in appear key={location.id}>
            <ClinicListRow
              // @ts-expect-error TS2322: Type '{ src: string; alt: null; }[]' is not assignable to type '{ src: string; alt?: string | undefined; }[]'.
              images={images}
              loading={loading}
              ref={isFirstLoc ? firstLocRef : null}
              key={location.id}
              location={location}
              soonestSlotTime={soonestSlot?.time}
              clampLocName
              onClick={onClick}
            />
          </Collapse>
        );
      })}
      <VirtualRow
        soonestVirtualSlotQuery={soonestVirtualSlotQuery}
        hideImages={!isVirtualOnly}
        apptReason={apptReason}
        rank={locations.length}
        viewId={viewId}
      />
    </ScrollContainer>
  );
};

export default React.memo(LocationList);
